import {
	Injectable,
	signal,
	WritableSignal
} from '@angular/core';
import {
	copyFormControl,
	CountryService,
	extractIDsFromArray,
	formatDateForServer,
	getChangedProperties,
	LocationRequest,
	PaginationService,
	RemoveDialogService,
	SimpleEntityService,
	SnackBarService
} from '@customcoding/shared';
import {PatientCreateRequest, PatientUpdateRequest} from '../../patient/patient.model';
import {HttpClient} from '@angular/common/http';
import {MinimalPatient, Patient} from '../models/patient.obj';
import {
	BehaviorSubject,
	firstValueFrom,
	forkJoin,
	lastValueFrom,
	Observable,
	of
} from 'rxjs';
import {UntypedFormArray, UntypedFormControl, UntypedFormGroup} from "@angular/forms";
import {SchoolService} from "./school.service";
import {Caretaker, CaretakerRequestData} from "../models/caretaker.obj";
import moment from "moment/moment";
import {catchError, defaultIfEmpty, map, tap} from "rxjs/operators";
import {Table} from "primeng/table";
import {Diagnosis, FlattenedDiagnosis} from "../models/diagnosis";
import {PatientProfileDataService} from "../../patient/patient-profile/patient-profile-data.service";
import {DiagnosisService} from "../../diagnosis/diagnosis.service";

@Injectable({
	providedIn: 'root'
})
export class PatientService extends SimpleEntityService<Patient, PatientCreateRequest, PatientUpdateRequest> {

	private minimalPatientDataSubject: BehaviorSubject<MinimalPatient[]> = new BehaviorSubject<MinimalPatient[]>([]);
	public minimalPatientData$: Observable<MinimalPatient[]> = this.minimalPatientDataSubject.asObservable();

	newDiagnosis: WritableSignal<number> = signal(null);
	newDiagnosisDate: WritableSignal<any> = signal(null);
	newDiagnosisDoctor: WritableSignal<string> = signal(null);
	newDiagnosisIsSuspicion: WritableSignal<boolean> = signal(false);
	diagnosesOfPatient: WritableSignal<FlattenedDiagnosis[]> = signal([]);
	diagnosesOfPatientToRemove: WritableSignal<FlattenedDiagnosis[]> = signal([]);

	constructor(
		http: HttpClient,
		pagination: PaginationService,
		private schoolService: SchoolService,
		private removeDialogService: RemoveDialogService,
		private countryService: CountryService,
		private snackBar: SnackBarService,
		private diagnosisService: DiagnosisService,
		private patientProfileDataService: PatientProfileDataService,
	) {
		super(http, pagination, '/api/scap/patient');

	}

	initDiagnosisSignals() {
		this.newDiagnosis.set(null);
		this.newDiagnosisDoctor.set(null);
		this.newDiagnosisDate.set(null);
		this.newDiagnosisIsSuspicion.set(false);
	}

	setDiagnosesOfPatientFromBackend(diagnoses: Diagnosis[]) {
		this.diagnosesOfPatient.set(diagnoses.map(diagnosis => {
			return {
				diagnosis_id: diagnosis.pivot?.diagnosis_id,
				description: diagnosis.description,
				icd10Code: diagnosis.icd10Code,
				icd11Code: diagnosis.icd11Code,
				isDisabled: diagnosis.isDisabled,
				isSuspicion: diagnosis.pivot?.isSuspicion,
				diagnosisDate: diagnosis.pivot?.diagnosisDate,
				diagnostician: diagnosis.pivot?.diagnostician,
				patient_id: diagnosis.pivot?.patient_id,
				pivot_id: diagnosis.pivot?.id,
				state: 'retained'
			}
		}));
	}

	async addTemporaryDiagnosis() {
		const newDiagnosis = await firstValueFrom(this.diagnosisService.getCached(this.newDiagnosis()))
		if (!newDiagnosis) {
			return;
		}
		this.diagnosesOfPatient.update(diagnosis => {
			return [
				...diagnosis,
				{
					diagnostician: this.newDiagnosisDoctor(),
					diagnosisDate: this.newDiagnosisDate(),
					isSuspicion: this.newDiagnosisIsSuspicion(),
					diagnosis_id: newDiagnosis.id,
					icd10Code: newDiagnosis.icd10Code,
					icd11Code: newDiagnosis.icd11Code,
					description: newDiagnosis.description,
					state: 'new'
				}
			]
		})
	}

	async removeTemporaryDiagnosis(index: number) {
		this.diagnosesOfPatient.update(diagnosis => {
			const removed = diagnosis.splice(index, 1);
			if(removed?.length && removed[0]?.state == 'retained') {
				this.diagnosesOfPatientToRemove.update(diagnoses => {
					return [...diagnoses, removed[0]];
				})
			}
			return diagnosis;
		})
	}

	applyTemplate(patient_id: number, template_id: number, selectedFolder: any = null): Observable<{}> {

		let apiUrl = `${this.apiEndpoint}/${patient_id}/template/${template_id}`;
		if (selectedFolder) {
			apiUrl += `/folder/${selectedFolder.id}`;
		}

		return this.http.post(apiUrl, {});
	}

	// ================================================================================================================
	// Caretakers handling
	removeCaretaker(index: number, caretakers: UntypedFormArray) {
		const caretaker = caretakers.at(index);

		if (this.schoolService.existsOnBackend(caretaker.value)) { // if caretaker exists on backend, delete him
			this.removeDialogService.openRemoveDialog('Sorgerechtsperson', caretaker.value.type, '/api/scap/caretaker/' + caretaker.value.id)
				.subscribe(() => caretakers.removeAt(index));
		} else {
			this.removeDialogService.openRemoveDialogWithoutRoute('Sorgerechtsperson', caretaker.value.type)
				.subscribe(() => caretakers.removeAt(index));
		}
	}

	copyCaretaker(caretaker: UntypedFormGroup, caretakers: UntypedFormArray) {
		const newFormGroup = copyFormControl(caretaker);

		// the copy does not exist in the backend yet
		newFormGroup.controls.id.setValue(`unsaved_${Math.random() * 1000000}`);
		newFormGroup.markAsDirty(); // new caretaker is dirty
		caretakers.markAsDirty({onlySelf: true}); // because a new caretaker was added

		caretakers.push(newFormGroup);
	}

	addCaretaker(caretaker: Caretaker, caretakers: UntypedFormArray): UntypedFormGroup {
		const birthdate = moment(caretaker.person.birthdate, "Y-MM-DD");

		const newCaretaker = new UntypedFormGroup({
			id: new UntypedFormControl(caretaker.id),
			type: new UntypedFormControl(caretaker.type),
			description: new UntypedFormControl(caretaker.description),
			isLivingAddress: new UntypedFormControl(Boolean(caretaker.isLivingAddress)),
			hasNoLocation: new UntypedFormControl(false),
			hasCustody: new UntypedFormControl(caretaker.hasCustody),
			workPhone: new UntypedFormControl(caretaker.workPhone),
			// person
			firstname: new UntypedFormControl(caretaker.person.firstname),
			lastname: new UntypedFormControl(caretaker.person.lastname),
			personEmail: new UntypedFormControl(caretaker.person.email),
			telephone: new UntypedFormControl(caretaker.person.telephone),
			mobile: new UntypedFormControl(caretaker.person.mobile),
			birthdate: new UntypedFormControl(birthdate.isValid() ? birthdate.toDate() : null),
			personLanguages: new UntypedFormControl(extractIDsFromArray(caretaker.person.languages)),

			community: new UntypedFormControl(caretaker.community),
			street: new UntypedFormControl(),
			houseNumber: new UntypedFormControl(),
			city: new UntypedFormControl(),
			postalCode: new UntypedFormControl(),
			country_short: new UntypedFormControl()
		});
		if (caretaker.person.location) {
			newCaretaker.patchValue({
				street: caretaker.person.location.street,
				houseNumber: caretaker.person.location.houseNumber,
				city: caretaker.person.location.city,
				postalCode: caretaker.person.location.postalCode,
				country_short: caretaker.person.location.country?.short,
			});
		} else {
			newCaretaker.patchValue({
				hasNoLocation: true,
			})
		}

		caretakers.push(newCaretaker);
		return newCaretaker;
	}

	createNewCaretaker(addCaretakerInput: UntypedFormControl, caretakers: UntypedFormArray) {
		if (addCaretakerInput.invalid) {
			addCaretakerInput.markAsTouched();
			return;
		}

		const newCaretaker = new UntypedFormGroup({
			id: new UntypedFormControl(`unsaved_${Math.random() * 1000000}`),
			type: new UntypedFormControl(addCaretakerInput.value),
			description: new UntypedFormControl(),
			isLivingAddress: new UntypedFormControl(false),
			hasNoLocation: new UntypedFormControl(false),
			hasCustody: new UntypedFormControl(false),
			workPhone: new UntypedFormControl(),
			// person
			firstname: new UntypedFormControl(),
			lastname: new UntypedFormControl(),
			personEmail: new UntypedFormControl(),
			telephone: new UntypedFormControl(),
			mobile: new UntypedFormControl(),
			personLanguages: new UntypedFormControl(),
			birthdate: new UntypedFormControl(),

			community: new UntypedFormControl(),

			street: new UntypedFormControl(),
			houseNumber: new UntypedFormControl(),
			city: new UntypedFormControl(),
			postalCode: new UntypedFormControl(),
			country_short: new UntypedFormControl(this.countryService.getDefaultCountry().short)
		});
		caretakers.push(newCaretaker);
		newCaretaker.markAsDirty(); // new caretaker is dirty
		caretakers.markAsDirty({onlySelf: true}); // because a new caretaker was added

		// reset addCaretakerInput
		addCaretakerInput.reset('Mutter'); // default value is needed, see `addCaretakerInput` declaration
		addCaretakerInput.markAsUntouched();
		addCaretakerInput.markAsPristine();
		addCaretakerInput.updateValueAndValidity();
	}

	async saveCaretakers(
		caretakers: UntypedFormArray,
		patientId: number,
		caretakerTable: Table
	) {
		let showNothingChangedMessage = true;
		return new Promise<boolean>((resolve) => {
			const observables: Observable<any>[] = [];

			for (const ct of caretakers.controls) {
				const caretaker = ct as UntypedFormGroup;
				let changedCaretakerData: CaretakerRequestData & Partial<LocationRequest>;
				if (this.schoolService.existsOnBackend(caretaker.value)) {
					changedCaretakerData = getChangedProperties(caretaker) as CaretakerRequestData;
					changedCaretakerData.hasNoLocation = caretaker.value.hasNoLocation;
				} else {
					changedCaretakerData = caretaker.value;
				}
				// fill out required fields
				changedCaretakerData.isLivingAddress = caretaker.value.isLivingAddress;
				changedCaretakerData.hasCustody = caretaker.value.hasCustody;
				changedCaretakerData.patient_id = patientId;
				if (changedCaretakerData.hasCustody || changedCaretakerData.isLivingAddress) {
					changedCaretakerData.community = caretaker.value.community;
				}

				if (changedCaretakerData.birthdate) {
					changedCaretakerData.birthdate = formatDateForServer(changedCaretakerData.birthdate)
				}

				// deleteNullProperties(changedCaretakerData); // backend does not like null
				if (!changedCaretakerData.birthdate) {
					delete changedCaretakerData.birthdate;
				}
				if (!(changedCaretakerData as any).personLanguages) {
					delete (changedCaretakerData as any).personLanguages;
				}
				if (!(changedCaretakerData as any).city) {
					delete (changedCaretakerData as any).city;
				}

				if (changedCaretakerData.hasNoLocation) {
					delete (changedCaretakerData as any).city;
					delete (changedCaretakerData as any).street;
					delete (changedCaretakerData as any).houseNumber;
					delete (changedCaretakerData as any).postalCode;
					delete (changedCaretakerData as any).country_short;
					changedCaretakerData.isLivingAddress = false;
				}

				if (caretaker.dirty) {
					showNothingChangedMessage = false;
					if (caretakerTable.isRowExpanded(caretaker)) {
						caretakerTable.toggleRow(caretaker);
					}
					if (this.schoolService.existsOnBackend(caretaker.value)) { // update existing caretaker
						if (changedCaretakerData.isLivingAddress || changedCaretakerData.hasCustody) {
							changedCaretakerData.community = caretaker.value.community;
						}
						observables.push(
							this.http.patch('/api/scap/caretaker/' + caretaker.value.id, changedCaretakerData).pipe(
								tap(() => caretaker.markAsPristine())
							)
						);
					} else { // create new caretaker
						if (!changedCaretakerData.community) {
							delete changedCaretakerData.community;
						}
						observables.push(
							this.http.post('/api/scap/caretaker', changedCaretakerData).pipe(
								tap((ct) => {
									caretaker.patchValue({id: ct.id});
									caretaker.markAsPristine();
								})
							)
						);
					}
				}
			}
			forkJoin(observables).pipe(defaultIfEmpty(null)).subscribe((allData) => {
				if (allData)
					this.snackBar.showSuccessDialog(`Sorgerechtsperson geändert.`);
				return resolve(showNothingChangedMessage);
			}, e => {
				this.snackBar.showServerError(`Sorgerechtsperson konnte nicht geändert werden.`, e);
				return resolve(showNothingChangedMessage);
			});
		});
	}

	// Assign patient to doctors
	assignPatientToUsers(patientId: number, userIds: number[]) {
		return this.http.patch(`/api/scap/patient/${patientId}/users`, {users: userIds});
	}

	checkPatientRegistrationNumberAvailability(registrationNumber: number) {
		return this.http.get(`/api/scap/patients/registration-number?registration_number=${registrationNumber}`);
	}

	saveAllPatientsOnInit(): Observable<MinimalPatient[]> {

		MoConsole.log("saving all patients on init");

		return this.http.get<MinimalPatient[]>('/api/scap/patient/minimal/data').pipe(
			tap(patients => {
				MoConsole.log("minimal patient data:::", patients);
				this.minimalPatientDataSubject.next(patients);
			})
		)
	}

	minAll(filter: string = ''): Observable<MinimalPatient[]> {
		return this.http.get<MinimalPatient[]>(`/api/scap/patient/minimal/data${filter}`);
	}

	filterUnavailable(selectedDate: any, editID: number): Observable<any> {

		if (!selectedDate) {
			return of([]);
		}

		const formattedStartDate =
			moment(selectedDate.startDate).format('YYYY-MM-DD') +
			'-' +
			moment({hours: selectedDate.startTime.hours, minutes: selectedDate.startTime.minutes}).format('HH:mm');

		const formattedEndDate =
			moment(selectedDate.endDate).format('YYYY-MM-DD') +
			'-' +
			moment({hours: selectedDate.endTime.hours, minutes: selectedDate.endTime.minutes}).format('HH:mm');

		return this.http.get<any[]>(`/api/scap/patients/not-available?startDate=${formattedStartDate}&endDate=${formattedEndDate}`).pipe(
			map(unavailable => unavailable.filter(patient => patient.pivot.appointment_id !== editID)),
			catchError(() => of([]))
		);
	}

	async saveDiagnoses() {
		if (this.patientProfileDataService.patient()?.id) {
			const newDiagnoses = this.diagnosesOfPatient();

			const valuesToRemove = this.diagnosesOfPatientToRemove();

			const valuesToAdd = newDiagnoses.filter(diagnosis => diagnosis.state == 'new');

			let observables: Observable<any>[] = []

			if (valuesToRemove?.length) {
				valuesToRemove.forEach(value => {
					observables.push(this.http.delete(`${this.apiEndpoint}/${this.patientProfileDataService.patient().id}/diagnosis-pivot/${value?.pivot_id}`))
				})
			}

			if (valuesToAdd?.length) {
				valuesToAdd.forEach(value => {
					const formData = new FormData();
					if (value.diagnosisDate) {
						formData.append('diagnosisDate', value.diagnosisDate);
					}
					if (value.diagnostician?.length) {
						formData.append('diagnostician', value.diagnostician);
					}
					formData.append('isSuspicion', value.isSuspicion ? '1' : '0');
					observables.push(this.http.post(`${this.apiEndpoint}/${this.patientProfileDataService.patient().id}/diagnosis/${value.diagnosis_id}`, formData));
				})
			}

			if(observables?.length) {
				await lastValueFrom(forkJoin(observables));
			} else {
				return false;
			}

			this.diagnosesOfPatient.update(diagnoses => {
				diagnoses = diagnoses.map(diagnosis => {
					return {
						...diagnosis,
						state: 'retained'
					}
				})
				return diagnoses;
			})

			this.diagnosesOfPatientToRemove.set([]);

			return true;

		} else {
			return false;
		}
	}
}
